import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useChatConfig } from "@/hooks";
import { useAgents } from "@/hooks/use-agents";
import { cn } from "@/lib/utils";
import type { AgentContextItem } from "@/types/agent-context";
import { agentToContextItem } from "@/types/agent-context";
import type { Agent } from "@/types/agent-types";
import { Attachment } from "@/types/chat-message";
import { useClickAway } from "@uidotdev/usehooks";
import { UIMessage } from "ai";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, AudioLines, Loader2, Mic, Paperclip, Square } from "lucide-react";
import type { ReasoningEffort } from "@taqat/core/types";
import type { ReasoningMode } from "./reasoning-effort-selector";
import { ReasoningEffortSelector } from "./reasoning-effort-selector";
import type React from "react";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { useLocalStorage, useWindowSize } from "usehooks-ts";
import { deleteFile, uploadChatAttachment } from "../../../services/upload-service";
import {
  getChromeStrings,
  isEmbedSurface,
  useEmbedLocale,
} from "@/lib/embed-locale";
import { PreviewAttachment } from "../file/preview-attachment";
import {
  ChatComposerEditor,
  type ChatComposerEditorRef,
  type ChatComposerSlashState,
} from "./chat-composer-editor";
import type { ComposerTokenAttrs } from "./chat-composer-token";
import { ActiveAgentChip } from "./active-agent-chip";
import { ContextPills } from "./context-pills";
import { DictationButton, type DictationButtonRef } from "./dictation-button";
import { MentionDropdown } from "./mention-dropdown";
import { MessageQueue, type MessageQueueItem } from "./message-queue";
import { FileMentionDropdown } from "@/features/file-agent/components/file-mention-dropdown";
import { FolderSelector } from "@/features/file-agent/components/folder-selector";
import { useFileIndex } from "@/features/file-agent/lib/use-file-index";
import type { FolderAttachment } from "@/features/file-agent/lib/use-folder-attachment";
import { RecordingOverlay } from "./recording-overlay";
import { mapAgentsToSlashOptions, mapSkillsToSlashOptions } from "./slash-command-data";
import { SlashCommandMenu } from "./slash-command-menu";
import type {
  SlashAgentOption,
  SlashCommandOption,
  SlashSkillOption,
} from "./slash-command-data";
import { SkillsService, type Skill } from "@/services/skills-service";

// Regex for detecting @ mentions
const MENTION_REGEX = /@(\w*)$/;

/**
 * Builds the final message body from the editor text and inline tokens.
 * Agent tokens stay in routing state while comments and commands are serialized into the prompt.
 */
function buildSubmittedInput({
  input,
  tokens,
}: {
  input: string;
  tokens: ComposerTokenAttrs[];
}) {
  const trimmedInput = input.trim();
  const commandTitles = tokens
    .filter((token) => token.kind === "command")
    .map((token) => token.value?.trim() || token.label.trim())
    .filter(Boolean);
  const promptComments = tokens
    .filter((token) => token.kind === "comment")
    .map((token) => token.value?.trim() || token.label.trim())
    .filter(Boolean);
  // Skill tokens carry the slug in `value` and the display name in `label`.
  // We surface both so the runtime can resolve the bundle by slug while the
  // LLM still sees a human-readable name in context.
  const seenSkills = new Set<string>();
  const skillEntries = tokens
    .filter((token) => token.kind === "skill")
    .map((token) => ({
      name: token.label.trim(),
      slug: token.value?.trim() || "",
    }))
    .filter((entry) => {
      const key = entry.slug || entry.name;
      if (!key || seenSkills.has(key)) return false;
      seenSkills.add(key);
      return Boolean(entry.name);
    });
  // File tokens carry their relative path in `value` — surface those to
  // the LLM as a list so it can call read_file (or skip if the path is
  // already obvious from context).
  const seenPaths = new Set<string>();
  const filePaths = tokens
    .filter((token) => token.kind === "file")
    .map((token) => token.value?.trim() || token.label.trim())
    .filter((path) => {
      if (!path || seenPaths.has(path)) return false;
      seenPaths.add(path);
      return true;
    });
  const sections = [
    commandTitles.length > 0 ? `Command: ${commandTitles.join(", ")}` : null,
    skillEntries.length > 0
      ? `Skills attached:\n${skillEntries
          .map((entry) => (entry.slug ? `- ${entry.name} (${entry.slug})` : `- ${entry.name}`))
          .join("\n")}`
      : null,
    promptComments.length > 0
      ? `Prompt notes:\n${promptComments.map((comment) => `- ${comment}`).join("\n")}`
      : null,
    filePaths.length > 0
      ? `Mentioned files (read with local_fs_read_file as needed):\n${filePaths.map((p) => `- ${p}`).join("\n")}`
      : null,
    trimmedInput || null,
  ].filter(Boolean);

  return sections.join("\n\n");
}

/**
 * Morphs between Send and Live (voice) buttons depending on whether the
 * composer has text/tokens to submit. Mirrors ChatGPT / Gemini behaviour:
 * the empty state offers a one-tap entry into a live voice conversation,
 * and the moment the user types, the same slot becomes the standard send
 * arrow. AnimatePresence does the visual swap so the transition reads as
 * a single button morphing rather than two buttons swapping out.
 *
 * `onLive` is what flips the parent into voice-session mode. When the
 * caller doesn't pass it (or `voiceLiveEnabled === false`), the button
 * always renders as Send and disables when there's nothing to submit —
 * keeping the legacy behaviour intact for chat surfaces that haven't
 * adopted live voice yet.
 */
function PureSendOrLiveButton({
  submitForm,
  canSubmit = false,
  uploadQueue,
  isDictating = false,
  hasContent,
  onLive,
  voiceLiveEnabled = false,
}: {
  submitForm: () => void;
  canSubmit: boolean;
  uploadQueue: string[];
  isDictating?: boolean;
  hasContent: boolean;
  onLive?: () => void;
  voiceLiveEnabled?: boolean;
}) {
  // Live mode is only offered when the composer is empty AND the caller
  // wired up an `onLive` handler. Otherwise we fall back to the classic
  // Send button so existing surfaces keep working unchanged.
  const showLive = voiceLiveEnabled && Boolean(onLive) && !hasContent && !isDictating;
  const uploading = (uploadQueue && uploadQueue.length > 0) ?? false;

  return (
    <div className="relative h-11 w-11 lg:h-8 lg:w-8">
      <AnimatePresence mode="wait" initial={false}>
        {showLive ? (
          <motion.div
            key="live"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  data-testid="live-button"
                  className="h-full w-full rounded-lg bg-primary text-primary-foreground p-0 hover:bg-primary/90"
                  onClick={(event) => {
                    event.preventDefault();
                    onLive?.();
                  }}
                  disabled={uploading}
                  aria-label="Start live voice conversation"
                >
                  <AudioLines className="inline-block size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Start live voice conversation</p>
              </TooltipContent>
            </Tooltip>
          </motion.div>
        ) : (
          <motion.div
            key="send"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <Button
              type="submit"
              data-testid="send-button"
              className="h-full w-full rounded-lg bg-primary text-primary-foreground p-0 hover:bg-primary/90"
              onClick={(event) => {
                event.preventDefault();
                submitForm();
              }}
              disabled={!canSubmit || uploading || isDictating}
            >
              {/* Mirror the arrow in RTL (Arabic embed) so it points toward the
                  send direction. rtl: keys off the nearest dir="rtl" ancestor,
                  so the main (LTR) app is unaffected. */}
              <ArrowRight className="inline-block size-5 rtl:-scale-x-100" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const SendOrLiveButton = memo(PureSendOrLiveButton, (prev, next) => {
  if (prev.uploadQueue.length !== next.uploadQueue.length) return false;
  if (prev.canSubmit !== next.canSubmit) return false;
  if (prev.isDictating !== next.isDictating) return false;
  if (prev.hasContent !== next.hasContent) return false;
  if (prev.voiceLiveEnabled !== next.voiceLiveEnabled) return false;
  if (prev.onLive !== next.onLive) return false;
  return true;
});

export type ChatInputProps = {
  /**
   * Input value. Used to set the chat input value from outside the component.
   * This is useful when you want to set the input value from the parent component.
   * For example, when you want to set the input value from the chat history or when
   * you want to set the input value from the chat suggestions.
   */
  value?: string;
  /**
   * Chat ID for the current chat session.
   */
  chatId: string;
  /**
   * Input text for the chat input.
   */
  input: string;
  /**
   * Function to set the input value.
   * This is called when the user types in the chat input.
   */
  setInput: (input: string) => void;
  /**
   * Indicates whether the chat input is currently loading.
   */
  isLoading: boolean;
  /**
   * CSS class name for the chat input component.
   * This can be used to apply custom styles to the chat input.
   */
  className?: string;

  /**
   * Attachment is enabled or not.
   */
  attachmentEnabled?: boolean;

  /**
   * Selected agents for multi-agent chat
   */
  selectedAgents?: Agent[];

  /**
   * Callback when agent selection changes
   */
  onAgentsChange?: (agents: Agent[]) => void;

  /**
   * Active sub-agent for the chat — drives the "Talking to: <Agent>" chip
   * above the composer and (in the parent container) the persistence/routing
   * for hand-offs from the Super-Agent. Null when no sub-agent is active.
   */
  activeAgent?: Agent | null;

  /**
   * Callback when the active sub-agent changes (slash pick, card click, or ×
   * on the chip). Pass `null` to clear and return control to the supervisor.
   */
  onActiveAgentChange?: (agent: Agent | null) => void;

  /**
   * Whether the user can hand the conversation to a different specialist via
   * the slash menu. Only meaningful inside a Super-Agent context — in a
   * direct sub-agent chat there is no router to delegate from.
   */
  allowAgentPicker?: boolean;

  /**
   * Array of messages in the chat.
   */
  messages?: UIMessage[];

  /**
   * Function to append a new message to the chat.
   */
  append?: (message: unknown, options?: unknown) => Promise<string | null | undefined>;

  /**
   * Submit function for the chat input.
   * This function is called when the user clicks the send button.
   */
  onSubmit?: (value: string, attachments?: Attachment[]) => void;

  /**
   * Function to be called when the user clicks the stop button.
   * The stop button is only shown when the model is streaming.
   */
  onStop?: () => void;

  /**
   * List of attachments for the chat session.
   */
  attachments: Array<Attachment>;

  /**
   * Function to set attachments for the chat session.
   */
  setAttachments: Dispatch<SetStateAction<Array<Attachment>>>;

  /**
   * Show notice hint below the chat input.
   * This is used to show a notice hint below the chat input.
   */
  showHint?: boolean;

  /**
   * Append "Powered by Taqat" to the hint line (embedded widget with
   * showBranding enabled — spec 075 Phase 3).
   */
  showEmbedBranding?: boolean;

  /**
   * Chat status for controlling UI state
   */
  status?: string;

  /**
   * Function to stop streaming
   */
  stop?: () => void;

  /**
   * Callback when tool panel is opened
   */
  onToolPanelOpen?: () => void;


  /**
   * Indicates if the input is being used in a compact context (like a widget).
   * When true, adjusts layout and spacing for narrow containers.
   */
  isCompact?: boolean;

  /**
   * Which side the slash / skill menu opens on. Defaults to "up" for the
   * messages view (input pinned to the bottom). The new-chat surface centers
   * the input with space below, so it passes "down".
   */
  slashMenuPlacement?: "up" | "down";

  /**
   * Optional slot for quick action buttons rendered in the input toolbar.
   * Placed after the attachment button and agent selector.
   */
  quickActions?: React.ReactNode;

  /**
   * Messages queued while the current assistant response is still streaming.
   * Rendered as a composer-attached tray directly above the prompt input.
   */
  queuedMessages?: MessageQueueItem[];

  /**
   * Removes a queued message before it is sent.
   */
  onRemoveQueuedMessage?: (id: string) => void;

  /**
   * Stops the current stream and sends a queued message next.
   */
  onSteerQueuedMessage?: (id: string) => void;

  /**
   * Current app ID for scoping specialist suggestions.
   */
  appId?: string;

  /**
   * Currently selected model ID for Main Assistant chat.
   * Only used when no specialist is selected.
   */
  selectedModel?: string;
  /**
   * Reasoning rungs the selected model supports, ascending. Resolved by the
   * caller because only it knows which model this turn will actually use.
   * Absent or shorter than two entries renders no control.
   */
  reasoningOptions?: ReasoningEffort[];
  /** How the selected model expresses reasoning: a ladder or a plain switch. */
  reasoningMode?: ReasoningMode;
  /** Current reasoning choice; undefined means the model's own default. */
  reasoningEffort?: ReasoningEffort;
  onReasoningEffortChange?: (effort: ReasoningEffort | undefined) => void;
  /** Model still resolving — the control holds its space with a skeleton. */
  reasoningLoading?: boolean;

  /**
   * Called when the user selects a different model.
   */
  onModelChange?: (model: string) => void;

  /**
   * Folder attachment context — only meaningful for browser-workspace
   * agents. When the user has attached a local folder, the composer's
   * `@` trigger opens a file-mention picker against this folder's index.
   */
  folderAttachment?: FolderAttachment;

  /**
   * Enables the Send ⇄ Live button morph. When true and `onLive` is
   * provided, the send slot renders the Live (voice) button while the
   * composer is empty and morphs back to Send the moment the user types.
   * Off by default so legacy chat surfaces keep their existing send-only
   * behaviour until they explicitly opt in.
   */
  voiceLiveEnabled?: boolean;

  /**
   * Called when the user taps the Live (voice) button. Parent surface is
   * responsible for opening the voice session UI (overlay, mic prompt,
   * WebSocket connect, etc.). The button is hidden unless this is set
   * AND `voiceLiveEnabled` is true.
   */
  onLive?: () => void;

  /**
   * Called when the user selects the `/compact` slash command. The parent
   * owns the compaction request and the chat's compaction state, so the
   * composer just signals intent. No-op when omitted.
   */
  onCompact?: () => void;

  /**
   * True while a `/compact` request is in flight. The composer is locked
   * (editor disabled, send disabled) and overlaid with a progress indicator
   * — sending a turn mid-compaction would race the summary write.
   */
  isCompacting?: boolean;

  /**
   * Host-supplied strip rendered directly beneath the composer box.
   *
   * A slot rather than a per-surface flag, for the same reason `quickActions`
   * is one: the Coding surface hangs its session bar (branch, diff stat,
   * sandbox state) here, and this component stays unaware that branches or
   * sandboxes exist. Every other surface omits it and renders nothing.
   */
  composerFooter?: React.ReactNode;

  /**
   * Prompt shown in the empty box.
   *
   * Defaults to the platform's. Surfaces that ask for something narrower than
   * "your prompt" — Coding asks for a task against a repository — override it.
   * The embed surface still wins, because its copy is locale-resolved and a
   * caller-supplied English string would undo that.
   */
  placeholder?: string;

};

/**
 * Uploads a file to the server for chat attachment using centralized upload service
 * The upload service properly handles backend URL resolution from environment variables
 * Files are uploaded to S3 bucket and can be accessed by the LLM for processing
 *
 * @param file - File to upload
 * @returns Promise resolving to uploaded file metadata or undefined
 */
const uploadFile = async (file: File, chatId?: string) => {
  // Use the centralized upload service that handles backend URL properly.
  // Passing `chatId` makes the server persist a `chat_attachments` row and
  // hand back a stable `fileId` — required for the agent's read_attachments
  // tool to resolve the file in later turns.
  const result = await uploadChatAttachment(file, chatId);

  // Return formatted attachment if upload was successful
  if (result.success && result.publicUrl && result.data) {
    return {
      url: result.publicUrl,
      name: result.data.filename,
      contentType: result.data.type,
      size: file.size,
      type: "file",
      fileId: result.fileId,
    } as Attachment;
  }

  // Error toast is already shown by the API client
  return undefined;
};

/**
 * Multimodal chat input component with agent selection
 *
 * Features:
 * - Multi-agent selection dropdown
 * - File attachments support
 * - Voice recording and transcription
 * - Video call integration
 * - Auto-resizing textarea
 * - Keyboard shortcuts (Enter to send, Shift+Enter for new line)
 * - LocalStorage persistence for input
 * - Loading states and error handling
 * - @ mention support for agent selection in compact mode
 *
 * @param props - Component configuration and callbacks
 * @returns JSX element for the multimodal input
 */
export function MultimodalInput({
  chatId,
  className,
  attachmentEnabled = true,
  isLoading = false,
  value = "",
  showHint = true,
  showEmbedBranding = false,
  attachments,
  setAttachments,
  onStop,
  onSubmit,
  selectedAgents = [],
  onAgentsChange,
  activeAgent = null,
  onActiveAgentChange,
  allowAgentPicker = true,
  input: propInput,
  setInput,
  stop,
  isCompact = false,
  slashMenuPlacement = "up",
  quickActions,
  queuedMessages = [],
  onRemoveQueuedMessage,
  onSteerQueuedMessage,
  appId,
  folderAttachment,
  voiceLiveEnabled = false,
  onLive,
  onCompact,
  isCompacting = false,
  composerFooter,
  placeholder,
  reasoningOptions,
  reasoningMode,
  reasoningEffort,
  onReasoningEffortChange,
  reasoningLoading,
}: ChatInputProps) {
  /**
   * CSS classes for styling the input wrapper
   * baseCls: Base styles for the wrapper element
   * focusCls: Focus styles with gradient border
   */
  const baseCls = "rounded-lg overflow-hidden w-full p-[1px] bg-border";
  const focusCls = "bg-gray-400 shadow-md shadow-gray-300/50";

  /**
   * Embedded-widget chrome localization (spec 075 — Phase 3): on the embed
   * surface the placeholder + disclaimer follow the session locale, and the
   * power-user placeholder (patterns/skills/files syntax) is replaced with a
   * plain "Type a message…" — those affordances aren't for anonymous visitors.
   * Off the embed surface this resolves to the exact strings used today.
   */
  const onEmbedSurface = isEmbedSurface();
  const embedLocaleValue = useEmbedLocale();
  const embedChrome = getChromeStrings(onEmbedSurface ? embedLocaleValue : null);
  const embedBranding = onEmbedSurface && showEmbedBranding;
  const composerPlaceholder = onEmbedSurface
    ? embedChrome.composerPlaceholder
    : (placeholder ??
      "Type your prompt - / for patterns, $ for skills, @ to search files");

  /**
   * Focus state management and click-away detection
   */
  const [isFocused, setIsFocused] = useState(false);
  const elRef = useClickAway<HTMLElement>(() =>
    setIsFocused(false)
  ) as React.RefObject<HTMLDivElement>;

  /**
   * Core component refs and state
   */
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerEditorRef = useRef<ChatComposerEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tokenControlledAgentIdsRef = useRef<string[]>([]);
  const preserveAgentSelectionOnClearRef = useRef(false);

  /**
   * UI state management
   */
  const { width } = useWindowSize();
  // Phone-sized viewport. Drives mobile-only composer behavior: Enter inserts
  // a newline (send via button) and focus is dropped after send so the soft
  // keyboard dismisses. Matches the <=768px branch already used for focus.
  const isMobileViewport = !!width && width <= 768;
  const [uploadQueue, setUploadQueue] = useState<string[]>([]);
  const [removingFiles, setRemovingFiles] = useState<Set<string>>(new Set());
  const [localStorageInput, setLocalStorageInput] = useLocalStorage("input", "");
  /**
   * Seed from the persisted draft in the initializer, not only in the mount
   * effect below. Starting at `""` left a window where the persist effect ran
   * first and wrote that `""` straight over the saved draft, so a reload threw
   * away whatever the user had typed.
   */
  const [input, setInputValue] = useState(
    () => propInput || value || localStorageInput || "",
  );
  const [isDictating, setIsDictating] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const isDraggingFilesRef = useRef(false);

  /**
   * Voice recording state management
   * recordingElapsedTime: Timer showing recording duration in seconds
   */
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0);
  const dictationButtonRef = useRef<DictationButtonRef>(null);

  /**
   * Agent mention context state for compact mode
   * selectedContextAgents: Local state tracking selected agents as context items
   * showMentionDropdown: Controls visibility of @ mention dropdown
   * mentionSearchQuery: Current search query from @ mention input
   */
  const [selectedContextAgents, setSelectedContextAgents] = useState<AgentContextItem[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchQuery, setMentionSearchQuery] = useState("");
  const [composerTokens, setComposerTokens] = useState<ComposerTokenAttrs[]>([]);
  const [slashMenuContext, setSlashMenuContext] = useState<ChatComposerSlashState>({
    open: false,
    trigger: "slash",
    mode: "root",
    query: "",
  });

  /**
   * - Fetch available agents for @ mentions
   * - Only fetches when isCompact=true (@ mention mode enabled)
   * - useAgents hook handles the agent fetching logic
   * - This replaces the need to pass agents as props to MentionDropdown
   */
  const { state: agentsState, fetchAgents } = useAgents({
    autoFetch: isCompact,
    initialParams: { limit: 100, appId },
  });

  /**
   * Chat configuration and persistent storage
   */
  useChatConfig();
  const hasContent = input.trim().length > 0 || composerTokens.length > 0;
  // Empty agent selection routes silently to SuperAgent on the backend; no
  // client-side gating required. Blocked while a `/compact` runs — sending a
  // turn mid-compaction would race the summary write and corrupt context.
  const canSubmit = hasContent && !isCompacting;

  const slashAgentOptions = useMemo(
    () => mapAgentsToSlashOptions(agentsState.agents as Agent[]),
    [agentsState.agents]
  );

  /**
   * Skills surfaced in the slash palette alongside agents. Lazily fetched
   * the first time the root menu opens — same trigger pattern as agents,
   * just keyed off a local state flag since we don't have a global store
   * for skills.
   */
  const [skillsState, setSkillsState] = useState<{
    loading: boolean;
    loaded: boolean;
    skills: Skill[];
  }>({ loading: false, loaded: false, skills: [] });

  const slashSkillOptions = useMemo(
    () => mapSkillsToSlashOptions(skillsState.skills),
    [skillsState.skills]
  );

  /**
   * - Sync selected context agents with parent selectedAgents
   * - When parent updates selectedAgents, sync to local context state
   * - Only runs in compact mode
   * - Ensures UI stays in sync with parent component state
   */
  useEffect(() => {
    if (isCompact && selectedAgents.length > 0) {
      const contextItems = selectedAgents.map((agent) => agentToContextItem(agent));
      setSelectedContextAgents(contextItems);
    }
  }, [selectedAgents, isCompact]);

  useEffect(() => {
    if (
      isCompact
      || !slashMenuContext.open
      || slashMenuContext.mode !== "agents"
      || agentsState.loading
      || agentsState.agents.length > 0
    ) {
      return;
    }

    void fetchAgents({ limit: 100, appId });
  }, [
    appId,
    agentsState.agents.length,
    agentsState.loading,
    fetchAgents,
    isCompact,
    slashMenuContext.mode,
    slashMenuContext.open,
  ]);

  /**
   * Mirrors the agent fetch above but for skills. Only the root menu shows
   * skills, so we don't bother loading them when the user dives into
   * `/agent` or `/comment`.
   *
   * `skillsState.loading` is intentionally NOT in deps — flipping it inside
   * the effect would re-run the effect, cleanup would set `cancelled = true`,
   * and the in-flight fetch's result would be discarded. `loaded` alone is
   * the right guard: once we've completed one round-trip we never refetch.
   */
  useEffect(() => {
    if (
      isCompact
      || !slashMenuContext.open
      || slashMenuContext.trigger !== "skill"
      || slashMenuContext.mode !== "skills"
      || skillsState.loaded
    ) {
      return;
    }

    let cancelled = false;
    setSkillsState((prev) =>
      prev.loading ? prev : { ...prev, loading: true },
    );
    (async () => {
      try {
        const [workspaceResp, platformResp] = await Promise.all([
          SkillsService.list("workspace"),
          SkillsService.list("platform"),
        ]);
        if (cancelled) return;
        setSkillsState({
          loading: false,
          loaded: true,
          skills: [
            ...workspaceResp.data.skills,
            ...platformResp.data.skills,
          ],
        });
      } catch (error) {
        // Surface the error so silent auth/permission failures don't look
        // like "skills feature is broken" — and mark loaded so we don't
        // retry on every keystroke.
        if (cancelled) return;
        console.error("Failed to load skills for dollar palette", error);
        setSkillsState({ loading: false, loaded: true, skills: [] });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isCompact,
    skillsState.loaded,
    slashMenuContext.mode,
    slashMenuContext.open,
    slashMenuContext.trigger,
  ]);

  /**
   * Adjusts the height of the textarea element to fit its content.
   *
   * This function sets the height of the textarea to 'auto' to reset any previous height,
   * and then sets it to the scrollHeight of the textarea plus 2 pixels to ensure all content is visible.
   * In compact mode, limits the maximum height to prevent the textarea from becoming too tall.
   *
   * @remarks
   * This function assumes that `textareaRef` is a reference to a textarea element.
   */
  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = textareaRef.current.scrollHeight + 2;
      // In compact mode, limit to 3 rows (~60px), otherwise allow up to 4 rows
      const maxHeight = isCompact ? 60 : 80;
      textareaRef.current.style.height = `${Math.min(newHeight, maxHeight)}px`;
    }
  }, [isCompact]);

  /**
   * Handle dictation transcript from the DictationButton component
   * Places the transcribed text directly in the input field
   */
  const handleDictationTranscript = useCallback(
    (transcript: string) => {
      if (transcript.trim()) {
        const newInput = input ? `${input} ${transcript.trim()}` : transcript.trim();
        setInputValue(newInput);
        setLocalStorageInput(newInput);
        setInput(newInput);

        // Adjust textarea height after adding transcript
        setTimeout(() => {
          if (textareaRef.current) {
            adjustHeight();
            textareaRef.current.focus();
            // Move cursor to end
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
          } else {
            composerEditorRef.current?.focus();
          }
        }, 0);
      }
    },
    [adjustHeight, input, setLocalStorageInput, setInput]
  );

  /**
   * Handle dictation start event
   * Resets timer for recording overlay
   */
  const handleDictationStart = useCallback(() => {
    setIsDictating(true);
    setRecordingElapsedTime(0);
  }, []);

  /**
   * Handle dictation stop event
   * Called when recording ends normally
   */
  const handleDictationStop = useCallback(() => {
    // Recording stopped - waiting for transcription or cancel
  }, []);

  /**
   * Timer effect for recording elapsed time
   */
  useEffect(() => {
    if (!isDictating || isTranscribing) {
      return;
    }

    const timer = setInterval(() => {
      setRecordingElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isDictating, isTranscribing]);

  /**
   * Handle cancel recording - discards recording and exits recording mode
   */
  const handleCancelRecording = useCallback(() => {
    // Cancel the recording (discards audio)
    dictationButtonRef.current?.cancelRecording();
    // Reset all recording state
    setIsDictating(false);
    setRecordingElapsedTime(0);
  }, []);

  /**
   * Handle confirm recording - transcribes audio and places text in input
   */
  const handleConfirmRecording = useCallback(async () => {
    try {
      setIsTranscribing(true);
      // Transcribe the recording - this stops recording, sends to API, and returns text
      await dictationButtonRef.current?.transcribeRecording();

      // The transcript is handled by handleDictationTranscript callback
      // Reset the recording UI state only after transcription completes
    } catch {
      // Error already handled in DictationButton with toast
    } finally {
      // Always reset states after transcription completes
      setIsDictating(false);
      setRecordingElapsedTime(0);
      setIsTranscribing(false);
    }
  }, []);

  /**
   * Handle dictation error - resets recording state when an error occurs
   */
  const handleDictationError = useCallback(() => {
    // Reset all recording state when an error occurs
    setIsDictating(false);
    setRecordingElapsedTime(0);
  }, []);

  /**
   * Attach a file to the chat input. Opens the file input dialog.
   * @param event The click event
   */
  const attachFile = (event: React.MouseEvent) => {
    event.preventDefault();
    // Ensure the file input is reset before opening
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setUploadQueue((currentQueue) => [
        ...currentQueue,
        ...files.map((file) => file.name),
      ]);

      try {
        const uploadedAttachments = await Promise.all(
          files.map((file) => uploadFile(file, chatId))
        );
        const successfulAttachments = uploadedAttachments.filter(
          (attachment): attachment is Attachment => attachment !== undefined
        );

        if (successfulAttachments.length > 0) {
          setAttachments((currentAttachments) => [
            ...currentAttachments,
            ...successfulAttachments,
          ]);
        }
      } catch {
        toast.error("Failed to upload file. Please try again.");
      } finally {
        setUploadQueue((currentQueue) =>
          currentQueue.filter((queuedName) => !files.some((file) => file.name === queuedName))
        );
      }
    },
    [chatId, setAttachments]
  );

  useEffect(() => {
    if (!attachmentEnabled) return;

    const isFileDrag = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types || []).includes("Files") ||
      Boolean(event.dataTransfer?.files?.length);

    const setDraggingFiles = (isDragging: boolean) => {
      isDraggingFilesRef.current = isDragging;
      setIsDraggingFiles(isDragging);
    };

    const isInsideComposer = (event: DragEvent) => {
      const composerElement = elRef.current;
      if (!composerElement) return false;

      const target = event.target;
      if (target instanceof Node && composerElement.contains(target)) {
        return true;
      }

      const bounds = composerElement.getBoundingClientRect();
      return (
        event.clientX >= bounds.left &&
        event.clientX <= bounds.right &&
        event.clientY >= bounds.top &&
        event.clientY <= bounds.bottom
      );
    };

    const handleNativeDragEnter = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      if (!isInsideComposer(event)) return;

      event.preventDefault();
      event.stopPropagation();
      setDraggingFiles(true);
    };

    const handleNativeDragOver = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      if (!isInsideComposer(event)) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setDraggingFiles(true);
    };

    const handleNativeDragLeave = (event: DragEvent) => {
      const composerElement = elRef.current;
      if (!composerElement) return;

      const nextTarget = event.relatedTarget as Node | null;
      if (nextTarget && composerElement.contains(nextTarget)) return;

      setDraggingFiles(false);
    };

    const handleNativeDrop = (event: DragEvent) => {
      if (!isFileDrag(event)) return;
      if (!isDraggingFilesRef.current && !isInsideComposer(event)) return;

      event.preventDefault();
      event.stopPropagation();
      setDraggingFiles(false);

      const files = Array.from(event.dataTransfer?.files || []);
      void uploadFiles(files);
    };

    document.addEventListener("dragenter", handleNativeDragEnter, true);
    document.addEventListener("dragover", handleNativeDragOver, true);
    document.addEventListener("dragleave", handleNativeDragLeave, true);
    document.addEventListener("drop", handleNativeDrop, true);

    return () => {
      document.removeEventListener("dragenter", handleNativeDragEnter, true);
      document.removeEventListener("dragover", handleNativeDragOver, true);
      document.removeEventListener("dragleave", handleNativeDragLeave, true);
      document.removeEventListener("drop", handleNativeDrop, true);
    };
  }, [attachmentEnabled, elRef, uploadFiles]);

  useEffect(() => {
    if (!attachmentEnabled) return;

    const isInsideComposer = (event: ClipboardEvent) => {
      const composerElement = elRef.current;
      const target = event.target;
      return Boolean(
        composerElement &&
        target instanceof Node &&
        composerElement.contains(target)
      );
    };

    const getClipboardFiles = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return [];

      const itemFiles = Array.from(clipboardData.items || [])
        .filter((item) => item.kind === "file")
        .map((item, index) => {
          const file = item.getAsFile();
          if (!file) return undefined;

          if (file.name) return file;

          const extension = file.type.split("/")[1] || "png";
          return new File([file], `pasted-image-${Date.now()}-${index}.${extension}`, {
            type: file.type || "image/png",
            lastModified: file.lastModified,
          });
        })
        .filter((file): file is File => file !== undefined);

      if (itemFiles.length > 0) return itemFiles;

      return Array.from(clipboardData.files || []);
    };

    const handleNativePaste = (event: ClipboardEvent) => {
      if (!isInsideComposer(event)) return;

      const files = getClipboardFiles(event);
      if (files.length === 0) return;

      event.preventDefault();
      event.stopPropagation();
      void uploadFiles(files);
    };

    document.addEventListener("paste", handleNativePaste, true);

    return () => {
      document.removeEventListener("paste", handleNativePaste, true);
    };
  }, [attachmentEnabled, elRef, uploadFiles]);

  const handleComposerTokensChange = useCallback((tokens: ComposerTokenAttrs[]) => {
    const nextAgentIds = tokens
      .filter((token) => token.kind === "agent" && token.id)
      .map((token) => token.id as string);
    const previouslyControlledIds = tokenControlledAgentIdsRef.current;
    const hadTokenControlledAgents = previouslyControlledIds.length > 0;

    setComposerTokens(tokens);

    if (nextAgentIds.length > 0) {
      const nextAgents = nextAgentIds
        .map((agentId) => agentsState.agents.find((agent) => agent.id === agentId))
        .filter((agent): agent is NonNullable<typeof agent> => agent !== undefined);

      // A newly-inserted agent token (the user just picked one from the slash
      // menu) promotes that agent to the conversation's active agent — the
      // selection persists across turns until the user clears the chip.
      const previousIds = new Set(previouslyControlledIds);
      const newlyAdded = nextAgents.filter((a) => !previousIds.has(a.id));
      if (newlyAdded.length > 0) {
        onActiveAgentChange?.(newlyAdded[newlyAdded.length - 1]);
      }

      tokenControlledAgentIdsRef.current = nextAgentIds;

      if (nextAgents.length > 0) {
        onAgentsChange?.(nextAgents);
      }

      return;
    }

    tokenControlledAgentIdsRef.current = [];

    // Note: do NOT clear the active agent here. Composer tokens vanish on
    // submit; the active agent is supposed to persist across turns and is
    // cleared explicitly by the user via the × on the chip.
    if (hadTokenControlledAgents && !preserveAgentSelectionOnClearRef.current) {
      onAgentsChange?.([]);
    }

    preserveAgentSelectionOnClearRef.current = false;
  }, [agentsState.agents, onAgentsChange, onActiveAgentChange]);

  const handleSlashMenuOpenChange = useCallback((open: boolean) => {
    if (open) {
      return;
    }

    if (isCompact) {
      setSlashMenuContext({ open: false, trigger: "slash", mode: "root", query: "" });
      return;
    }

    composerEditorRef.current?.clearSlashQuery();
    composerEditorRef.current?.focus();
    setSlashMenuContext({ open: false, trigger: "slash", mode: "root", query: "" });
  }, [isCompact]);

  const handleSlashCommandSelect = useCallback((command: SlashCommandOption) => {
    if (command.nextMode === "agents") {
      setSlashMenuContext((prev) => ({ ...prev, open: true, mode: "agents", query: "" }));
      composerEditorRef.current?.focus();
      return;
    }

    if (command.id === "compact") {
      composerEditorRef.current?.clearSlashQuery();
      composerEditorRef.current?.focus();
      onCompact?.();
      return;
    }

    composerEditorRef.current?.insertCommandToken(command);
  }, [onCompact]);

  const handleSlashAgentSelect = useCallback((agentOption: SlashAgentOption) => {
    composerEditorRef.current?.insertAgentToken(agentOption);
  }, []);

  const handleSlashSkillSelect = useCallback((skillOption: SlashSkillOption) => {
    composerEditorRef.current?.insertSkillToken(skillOption);
  }, []);

  const handleSlashCommentSelect = useCallback((comment: string) => {
    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      return;
    }

    composerEditorRef.current?.insertCommentToken(trimmedComment);
  }, []);

  /**
   * File-mention picker — fires the `@` trigger only when this chat's
   * agent has a browser workspace with an attached folder. The hook
   * walks the folder once on attach and caches a flat path index for
   * the dropdown to filter against.
   */
  const fileIndexResult = useFileIndex(folderAttachment?.handle ?? null);

  const handleFileMentionSelect = useCallback(
    (file: { name: string; path: string }) => {
      composerEditorRef.current?.insertFileToken(file);
      setSlashMenuContext({ open: false, trigger: "slash", mode: "root", query: "" });
    },
    [],
  );

  const handleFileMentionOpenChange = useCallback((open: boolean) => {
    if (open) return;
    composerEditorRef.current?.clearSlashQuery();
    composerEditorRef.current?.focus();
    setSlashMenuContext({ open: false, trigger: "slash", mode: "root", query: "" });
  }, []);

  /**
   * Adjust the height of the textarea on mount.
   * This is necessary to ensure that the textarea is the correct height when the component is first rendered.
   */
  useEffect(() => {
    if (textareaRef.current) {
      // Set the initial height of the textarea to fit its content
      adjustHeight();
    }
  }, [adjustHeight]);

  /**
   * Auto-focus the editor on mount (desktop only).
   *
   * `chat.tsx` renders TWO MultimodalInput instances behind a conditional
   * branch — one inside `<ChatEmptyState>` for the "What can I do for you?"
   * surface, and a separate one for the messages-view. When the first
   * message in a new chat lands and `messages.length > 0` flips, the
   * empty-state input unmounts and a fresh editor mounts. Without this,
   * focus dies in the handoff and the user has to click the input again.
   * The mount focus is gated on `width > 768` (skip mobile, where stealing
   * focus pops the on-screen keyboard) and runs at most once per mount.
   */
  const didAutoFocusRef = useRef(false);
  useEffect(() => {
    if (didAutoFocusRef.current) return;
    if (!width || width <= 768) return;
    didAutoFocusRef.current = true;
    // Defer one tick so Tiptap is fully mounted before we grab focus.
    const t = setTimeout(() => {
      if (isCompact) {
        textareaRef.current?.focus();
      } else {
        composerEditorRef.current?.focus();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [width, isCompact]);

  useEffect(() => {
    const domValue = textareaRef.current?.value || "";
    // Prefer DOM value over localStorage to handle hydration
    const finalValue = domValue || localStorageInput || "";
    setInputValue(finalValue);
    setInput(finalValue);
    if (textareaRef.current) {
      adjustHeight();
    }
    // Only run once after hydration
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalStorageInput(input);
  }, [input, setLocalStorageInput]);

  // Sync with parent input state
  useEffect(() => {
    setInputValue(propInput || value || "");
  }, [propInput, value]);

  const handleComposerTextChange = useCallback((text: string) => {
    setInputValue(text);
    setInput(text);
  }, [setInput]);

  const handleComposerSlashStateChange = useCallback((state: ChatComposerSlashState) => {
    setSlashMenuContext(state);
  }, []);

  /**
   * This function is used to handle the input event of the textarea element.
   * It updates the input state with the value of the textarea and adjusts its height to fit the content.
   * The height is adjusted by setting the height of the textarea to 'auto'
   * and then setting it to the scrollHeight of the textarea plus 2 pixels.
   * 
   * In compact mode, also checks for @ mention trigger to show agent dropdown
   * @param event The change event for the textarea element.
   * This function updates the input state with the value of the textarea and adjusts its height.
   * It is called whenever the user types in the textarea.
   */
  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setInput(newValue);
    adjustHeight();

    // Check for @ mention trigger (only in compact mode)
    if (isCompact) {
      const cursorPosition = event.target.selectionStart;
      const textBeforeCursor = newValue.slice(0, cursorPosition);
      const mentionMatch = textBeforeCursor.match(MENTION_REGEX);

      if (mentionMatch) {
        // Show dropdown and extract search query after @
        setShowMentionDropdown(true);
        setMentionSearchQuery(mentionMatch[1]);
      } else {
        // Hide dropdown if @ mention pattern not found
        setShowMentionDropdown(false);
        setMentionSearchQuery("");
      }
    }
  };

  /**
   * - Handle agent selection from mention dropdown
   * - Adds selected agent to selectedContextAgents if not already present
   * - Converts context items back to Agent[] and notifies parent via onAgentsChange
   * - Removes the @mention text from input
   * - Repositions cursor after mention removal
   * 
   * @param item - Selected agent context item from dropdown
   */
  const handleAgentMentionSelect = useCallback(
    (item: AgentContextItem) => {
      // Add to selected context agents if not already selected
      if (!selectedContextAgents.find((i) => i.id === item.id)) {
        const updatedAgents = [...selectedContextAgents, item];
        setSelectedContextAgents(updatedAgents);

        // Convert context items back to agents and update parent
        const agents = updatedAgents
          .map((contextItem) =>
            agentsState.agents.find((agent) => agent.id === contextItem.id)
          )
          .filter((agent) => agent !== undefined);

        onAgentsChange?.(agents);
      }

      // Remove the @mention from input
      if (textareaRef.current) {
        const cursorPosition = textareaRef.current.selectionStart;
        const textBeforeCursor = input.slice(0, cursorPosition);
        const textAfterCursor = input.slice(cursorPosition);
        const mentionStart = textBeforeCursor.lastIndexOf("@");

        const newValue = input.slice(0, mentionStart) + textAfterCursor;
        setInputValue(newValue);
        setInput(newValue);

        // Reset cursor position after mention removal
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = mentionStart;
            textareaRef.current.selectionEnd = mentionStart;
            textareaRef.current.focus();
          }
        }, 0);
      }

      setShowMentionDropdown(false);
      setMentionSearchQuery("");
    },
    [input, selectedContextAgents, setInput, agentsState.agents, onAgentsChange]
  );

  /**
   * - Handle context agent removal from pills
   * - Removes agent from selectedContextAgents local state
   * - Updates parent component with remaining agents via onAgentsChange
   * 
   * @param id - ID of the agent to remove
   */
  const handleContextAgentRemove = useCallback(
    (id: string) => {
      const updatedAgents = selectedContextAgents.filter((item) => item.id !== id);
      setSelectedContextAgents(updatedAgents);

      // Update parent with remaining agents
      const agents = updatedAgents
        .map((contextItem) =>
          agentsState.agents.find((agent) => agent.id === contextItem.id)
        )
        .filter((agent): agent is NonNullable<typeof agent> => agent !== undefined);

      onAgentsChange?.(agents);
    },
    [selectedContextAgents, agentsState.agents, onAgentsChange]
  );

  /**
   * Event handler for when the user selects a file to upload.
   * This will upload the file to the server and add it to the attachments array.
   */
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      // Get the files from the input element
      const files = Array.from(event.target.files || []);

      // Reset the file input value to allow re-selecting the same file
      if (event.target) {
        event.target.value = "";
      }

      await uploadFiles(files);
    },
    [uploadFiles]
  );

  /**
   * Removes an attachment from the attachments list.
   * This handles both frontend removal and backend cleanup.
   * @param attachmentToRemove - The attachment to remove
   */
  const handleRemoveAttachment = useCallback(
    async (attachmentToRemove: Attachment) => {
      // Add to removing files set to show loading state
      setRemovingFiles((prev) => new Set([...prev, attachmentToRemove.url]));

      try {
        // Call backend to delete the file from S3
        const result = await deleteFile(attachmentToRemove.url);

        if (result.success) {
          // Remove from attachments list on successful backend deletion
          if (setAttachments) {
            setAttachments((currentAttachments) =>
              currentAttachments.filter(
                (attachment) => attachment.url !== attachmentToRemove.url
              )
            );
          }
          toast.success("File removed successfully");
        } else {
          // Show error if backend deletion failed
          toast.error(result.error || "Failed to remove file");
        }
      } catch {
        toast.error("Failed to remove file. Please try again.");
      } finally {
        // Remove from removing files set
        setRemovingFiles((prev) => {
          const updated = new Set(prev);
          updated.delete(attachmentToRemove.url);
          return updated;
        });
      }
    },
    [setAttachments]
  );

  /**
   * Submit the form. This function is called when the user clicks the send button.
   * When the new chat, this will move the navition to
   * corresponding chat page.
   * 
   * NEW: Also clears selectedContextAgents on submit
   */
  const submitForm = useCallback(() => {
    if (!canSubmit) {
      return;
    }

    if (!isCompact && slashMenuContext.open) {
      toast.info("Choose a menu option before sending.");
      return;
    }

    const submission = isCompact
      ? { text: input, tokens: [] as ComposerTokenAttrs[] }
      : composerEditorRef.current?.getSubmission() ?? { text: input, tokens: composerTokens };
    const submittedInput = buildSubmittedInput({
      input: submission.text,
      tokens: submission.tokens,
    });

    if (!submittedInput.trim()) {
      return;
    }

    if (onSubmit) {
      onSubmit(submittedInput, attachments);
    }

    // Clear the attachment array as that will be appears in the message
    // itself. So no need to show this next to the input
    if (setAttachments) {
      setAttachments([]);
    }

    // Clear context agents on submit
    setSelectedContextAgents([]);
    setSlashMenuContext({ open: false, trigger: "slash", mode: "root", query: "" });

    // Clear the local storage input
    setLocalStorageInput("");

    // Clear the input value
    setInputValue("");
    setInput("");
    setComposerTokens([]);

    if (!isCompact) {
      preserveAgentSelectionOnClearRef.current = tokenControlledAgentIdsRef.current.length > 0;
      composerEditorRef.current?.clear();
    }

    // For desktop, focus on the textarea after submitting the form.
    //
    // Deferred to the next macrotask on purpose: the send button is the
    // current activeElement, and the state updates above (input clear,
    // editor `clear()` → `setContent`, parent `setMessages`) haven't been
    // committed yet. A synchronous `.focus()` here gets undone the moment
    // React commits and Tiptap reconciles content. setTimeout(..., 0)
    // matches the pattern already used in `handleDictationTranscript`.
    if (width && width > 768) {
      setTimeout(() => {
        if (isCompact) {
          textareaRef.current?.focus();
        } else {
          composerEditorRef.current?.focus();
        }
      }, 0);
    } else {
      // Mobile (iOS/Android): drop focus from the composer so the soft
      // keyboard dismisses and the freshly sent message + the assistant's
      // response are visible. Sending via the on-screen Return key keeps the
      // editor focused (unlike a Send-button tap, which blurs it), so the
      // keyboard would otherwise stay pinned open. Deferred a tick to run
      // after React commits the post-submit clear — same reasoning as the
      // desktop refocus branch above.
      setTimeout(() => {
        if (isCompact) {
          textareaRef.current?.blur();
        } else {
          composerEditorRef.current?.blur();
        }
      }, 0);
    }
  }, [
    attachments,
    canSubmit,
    composerTokens,
    hasContent,
    input,
    isCompact,
    onSubmit,
    setLocalStorageInput,
    setInput,
    setAttachments,
    slashMenuContext.open,
    width,
  ]);

  /**
   * Handle the key down event for the textarea. If the key is the enter key,
   * and the shift key is not pressed, submit the form. Otherwise, do nothing.
   * @param e The key up event
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isCompact) {
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      // Mobile: let the Return key insert a newline; sending is via the
      // Send button there. Desktop keeps Enter-to-send. The embedded widget
      // is narrow (iframe ~384px) but is a desktop-style keyboard surface —
      // Enter must SEND there (spec 075), so it opts out of the mobile rule.
      if (isMobileViewport && !onEmbedSurface) {
        return;
      }
      // Prevent the default behavior of the enter key So that
      // it won't come to the next line
      e.preventDefault();
      submitForm();
    }

    // Resize the textarea to fit the content if the enter key is pressed
    // along with the shift key
    if (e.key === "Enter" && e.shiftKey) {
      if (!textareaRef.current) {
        return;
      }
      const textarea = textareaRef.current as HTMLTextAreaElement;
      textarea.style.height = "auto";

      // In compact mode, limit to 3 rows (~60px), otherwise allow up to 4 rows (~80px)
      const maxHeight = isCompact ? 60 : 80;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  };

  /**
   * Stop the model from streaming.
   * This function is called when the user clicks the stop button.
   * @param event
   */
  const stopStreaming = (event: React.MouseEvent) => {
    event.preventDefault();
    if (onStop) {
      onStop();
    }
    if (stop) {
      stop();
    }
  };

  function handleSubmit(
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) {
    e.preventDefault();
    e.stopPropagation();

    // Submit chat message
    submitForm();
  }

  return (
    <div className={cn("relative mx-auto flex w-full flex-col gap-2", isCompact ? "py-2" : "py-4", isCompact ? "max-w-full" : "max-w-full")}>
      {/*
        Compaction lock — while `/compact` runs, the composer is overlaid and
        non-interactive (the editor is also `disabled` and send is gated off).
        Sending a turn mid-compaction would race the summary write, so the
        input is fully locked until the request settles. Compaction is quick,
        so this is a brief, self-clearing state.
      */}
      {isCompacting && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-background/70 backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm">
            <Loader2 className="size-3.5 animate-spin" />
            Compacting conversation…
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex w-full gap-2">
        <div className="relative flex w-full flex-col gap-4">
          {/* 
            Hidden file input for attachment functionality
            Triggered programmatically by the attachment button
          */}
          <input
            type="file"
            className="pointer-events-none fixed -left-4 -top-4 size-0.5 opacity-0"
            ref={fileInputRef}
            multiple
            tabIndex={-1}
            onChange={handleFileChange}
          />

          {/* 
            - Mention dropdown for compact mode
            - Only rendered when isCompact=true
            - Fetches agents internally, no need to pass as props
            - Shows when user types @ in textarea
          */}
          {isCompact && (
            <MentionDropdown
              open={showMentionDropdown}
              onOpenChange={setShowMentionDropdown}
              searchQuery={mentionSearchQuery}
              onSelect={handleAgentMentionSelect}
              anchorElement={textareaRef.current}
            />
          )}

          {!isCompact && (
            <SlashCommandMenu
              open={
                slashMenuContext.open
                && (slashMenuContext.trigger === "slash" || slashMenuContext.trigger === "skill")
                && !isDictating
                && !isTranscribing
              }
              mode={slashMenuContext.mode}
              query={slashMenuContext.query}
              anchorElement={composerEditorRef.current?.getAnchorElement() || null}
              placement={slashMenuPlacement}
              agentOptions={slashAgentOptions}
              agentsLoading={agentsState.loading}
              skillOptions={slashSkillOptions}
              skillsLoading={skillsState.loading}
              onOpenChange={handleSlashMenuOpenChange}
              onCommandSelect={handleSlashCommandSelect}
              onAgentSelect={handleSlashAgentSelect}
              showAgentCommand={allowAgentPicker}
              onSkillSelect={handleSlashSkillSelect}
              onCommentSelect={handleSlashCommentSelect}
            />
          )}

          {/**
           * File-mention dropdown — only meaningful when the chat is on
           * a browser-workspace agent with an attached, granted folder.
           * Falls back to nothing when the trigger isn't `@` or the
           * folder isn't ready, so the slash menu and other inputs
           * behave exactly as before for non-browser-workspace chats.
           */}
          {!isCompact
            && folderAttachment?.supported
            && folderAttachment.handle
            && !folderAttachment.needsReconnect && (
            <FileMentionDropdown
              open={
                slashMenuContext.open
                && slashMenuContext.trigger === "mention"
                && !isDictating
                && !isTranscribing
              }
              searchQuery={slashMenuContext.query}
              anchorElement={composerEditorRef.current?.getAnchorElement() || null}
              index={fileIndexResult.index}
              loading={fileIndexResult.loading}
              onSelect={handleFileMentionSelect}
              onOpenChange={handleFileMentionOpenChange}
            />
          )}

          {/* 
            Main chat input container with focus states
            Contains textarea, buttons, and agent selector
          */}
          <div className="flex flex-col">
            <MessageQueue
              items={queuedMessages}
              onRemove={onRemoveQueuedMessage ?? (() => undefined)}
              onSteer={onSteerQueuedMessage ?? (() => undefined)}
            />
            {/* Hand-off indicator: visible whenever the chat is routed to a
                specialist (slash pick or empty-state card). × clears back to
                the supervisor. See active-agent-chip.tsx. */}
            {activeAgent && (
              <ActiveAgentChip
                agent={activeAgent}
                onClear={() => onActiveAgentChange?.(null)}
                disabled={isLoading}
              />
            )}
            <div
              ref={elRef}
              className={cn(
                // `relative z-10` keeps this box painting ABOVE `composerFooter`,
                // which is pulled up behind it by a negative margin. Without the
                // stacking order the later sibling would cover the composer's
                // bottom edge instead of tucking under it.
                baseCls,
                "relative z-10",
                isFocused ? focusCls : "",
                isDraggingFiles ? "bg-primary shadow-md shadow-primary/20" : "",
                className,
              )}
            >
            <div className="overflow-hidden rounded-lg bg-background">
              {(uploadQueue.length > 0 || attachments.length > 0 || isDraggingFiles) && (
                <div
                  className={cn(
                    "px-3 py-3 transition-colors",
                    isDraggingFiles ? "bg-primary/5" : "bg-muted/20",
                  )}
                >
                  {isDraggingFiles ? (
                    <div className="flex min-h-[88px] items-center justify-center rounded-lg border border-dashed border-primary/60 bg-background/80 px-4 text-sm font-medium text-primary">
                      Drop files here to attach them
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-start gap-2">
                      {attachments.map((attachment) => (
                        <PreviewAttachment
                          key={attachment.url}
                          attachment={attachment}
                          onRemove={handleRemoveAttachment}
                          showRemoveButton={true}
                          isRemoving={removingFiles.has(attachment.url)}
                          variant="composer"
                        />
                      ))}
                      {uploadQueue.map((filename) => (
                        <PreviewAttachment
                          key={filename}
                          isUploading={true}
                          attachment={{
                            url: "",
                            name: filename,
                            contentType: "",
                            size: 0,
                            type: "file",
                          }}
                          showRemoveButton={false}
                          variant="composer"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/*
                - Show context pills when agents are selected in compact mode
                - Pills appear above the textarea
                - Only visible in compact mode
                - Shows selected agents as removable badges
              */}
              {isCompact && selectedContextAgents.length > 0 && !isDictating && (
                <div className="px-2 pt-2">
                  <ContextPills
                    items={selectedContextAgents}
                    onRemove={handleContextAgentRemove}
                  />
                </div>
              )}

              <div className="relative block">
                {isDictating || isTranscribing ? (
                  /* Recording overlay - replaces textarea during voice recording and transcription */
                  <RecordingOverlay
                    isRecording={isDictating}
                    elapsedTime={recordingElapsedTime}
                    onCancel={handleCancelRecording}
                    onConfirm={handleConfirmRecording}
                    isCompact={isCompact}
                    isTranscribing={isTranscribing}
                  />
                ) : (
                  <>
                    {isCompact ? (
                      <div
                        className={cn(
                          "flex flex-wrap items-start gap-2",
                          "px-1.5 py-1.5"
                        )}
                      >
                        <textarea
                          className={cn(
                            "min-w-[160px] flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground resize-none",
                            "min-h-[44px] max-h-[120px] p-0 text-base leading-6 lg:min-h-[32px] lg:max-h-[60px] lg:text-sm lg:leading-5"
                          )}
                          placeholder={composerPlaceholder}
                          rows={1}
                          value={input}
                          ref={textareaRef}
                          onChange={handleInput}
                          onFocus={() => setIsFocused(true)}
                          onKeyDown={handleKeyDown}
                        />
                      </div>
                    ) : (
                      <div
                        className="max-h-[min(40vh,360px)] overflow-y-auto px-4 py-2"
                        onFocusCapture={() => setIsFocused(true)}
                      >
                        <ChatComposerEditor
                          ref={composerEditorRef}
                          value={input}
                          tokens={composerTokens}
                          placeholder={composerPlaceholder}
                          isCompact={false}
                          onTextChange={handleComposerTextChange}
                          onTokensChange={handleComposerTokensChange}
                          onSlashStateChange={handleComposerSlashStateChange}
                          onSubmitShortcut={submitForm}
                          onFocus={() => setIsFocused(true)}
                          disabled={isCompacting}
                          enterSubmits={!isMobileViewport || onEmbedSurface}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              {!isDictating && (
              <div className={cn("flex items-center gap-2", isCompact ? "p-0.5" : "px-3 py-1")}>
                {/**
                  Attachment button — gated on the agent's `Accept Files`
                  toggle (`attachmentEnabled`). When the agent doesn't accept
                  files, the button is hidden entirely on both the empty-state
                  and messages-view surfaces, keeping behavior consistent
                  across the chat lifecycle.
                */}
                {attachmentEnabled && !isDictating && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center justify-center rounded-full text-foreground",
                          "transition-all hover:bg-muted",
                          "h-11 w-11 lg:h-8 lg:w-8"
                        )}
                        onClick={attachFile}
                        aria-label="Attach file"
                      >
                        <Paperclip className="size-4 shrink-0" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Attach file</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/**
                  Folder selector chip — only meaningful for browser-workspace
                  agents. Renders the active folder, lets the user switch
                  between previously-attached folders, and exposes
                  reconnect / attach-new / remove from a single chip in the
                  toolbar so the chat surface stays uncluttered.
                */}
                {!isCompact && folderAttachment?.supported && (
                  <FolderSelector
                    attachment={folderAttachment}
                    disabled={false}
                  />
                )}

                <div className="flex-1" />

                {/* Right side action buttons - hidden when recording (buttons are in overlay) */}
                {!isDictating && (
                  <div className="flex items-center text-muted-foreground">
                    {/**
                      Thinking control. Grouped with the send-side actions
                      rather than with attach: attach and folder describe what
                      the message CARRIES, while this describes how the reply
                      gets PRODUCED, so it belongs next to the button that
                      produces it. It removes itself when the model offers
                      nothing to choose.

                      Locked while a reply is streaming: the setting is read
                      when the turn is sent, so changing it mid-stream would
                      silently do nothing to the answer on screen while the
                      control claimed otherwise. It applies from the next turn,
                      and the lock makes that legible rather than leaving the
                      user to guess.
                    */}
                    <ReasoningEffortSelector
                      options={reasoningOptions}
                      mode={reasoningMode}
                      value={reasoningEffort}
                      onChange={onReasoningEffortChange ?? (() => {})}
                      disabled={isLoading || !onReasoningEffortChange}
                      loading={reasoningLoading}
                    />

                    {/* Quick actions slot (e.g. form dropdown) */}
                    {quickActions}

                    {/* Visible mic button for starting recording. Hidden on the
                        embedded/bubble surface — the widget is text-only there
                        (no voice dictation). Still shown in the main app chat. */}
                    {!onEmbedSurface && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              'flex items-center justify-center rounded-full h-11 w-11 lg:h-8 lg:w-8 text-sm transition-all duration-200 hover:bg-muted',
                              'text-muted-foreground hover:text-foreground'
                            )}
                            onClick={() => {
                              // Start recording via the hidden DictationButton ref
                              dictationButtonRef.current?.startRecording();
                            }}
                            disabled={false}
                          >
                            <Mic className="inline-block size-5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Start voice recording</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    <div className="ml-2" />

                    {isLoading && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="Stop response"
                            onClick={stopStreaming}
                            className={cn(
                              "flex items-center justify-center rounded-lg h-11 w-11 lg:h-8 lg:w-8",
                              // Same orange as the Send button — clearly an active
                              // control, with a stop glyph instead of the arrow.
                              "bg-primary text-primary-foreground cursor-pointer shadow-sm",
                              "transition-all hover:bg-primary/90 active:scale-95",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                            )}
                          >
                            <Square className={cn("fill-current", isCompact ? "size-3" : "size-4")} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Stop response</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {(!isLoading || hasContent) && (
                      <SendOrLiveButton
                        submitForm={submitForm}
                        canSubmit={canSubmit}
                        uploadQueue={uploadQueue}
                        isDictating={isDictating}
                        hasContent={hasContent}
                        voiceLiveEnabled={voiceLiveEnabled}
                        onLive={onLive}
                      />
                    )}
                  </div>
                )}
              </div>
                )}
            </div>
          </div>
          {/*
            Host-supplied tray, tucked under the composer: inset on both sides
            and pulled up by `-mt-3` so its top edge disappears behind the box
            above (which carries `z-10` for exactly that). The matching `pt-3`
            puts the content back below the seam. The slot owns its own
            horizontal padding so a host can render a full-bleed strip.
          */}
          {composerFooter ? (
            <div className="relative z-0 mx-2 -mt-3 rounded-b-lg border border-t-0 border-border/60 bg-muted/70 pt-3">
              {composerFooter}
            </div>
          ) : null}
          </div>
        </div>
      </form>

      {showHint && !isCompact && (
        <p className="type-meta px-4 text-center text-muted-foreground">
          {embedChrome.disclaimer}
          {embedBranding ? <> · {embedChrome.poweredBy}</> : null}
        </p>
      )}

      {/*
        Hidden DictationButton - keeps ref active for transcription even when overlay is shown.
        The button itself is visually hidden but mounted to maintain the recording state.
        Placed outside the conditional toolbar to stay mounted during dictation.
      */}
      <div className="hidden">
        <DictationButton
          ref={dictationButtonRef}
          onTranscript={handleDictationTranscript}
          onDictationStart={handleDictationStart}
          onDictationStop={handleDictationStop}
          onDictationError={handleDictationError}
          disabled={false}
          language="en"
        />
      </div>
    </div>
  );
}
